import type { LegalDocumentKey } from '@hue/contracts';
import { api } from './api.js';

/// A conta da pessoa (ADR 0067): o cadastro público e "Minha conta".
export interface CustomerAccount {
  email: string;
  firstName: string;
  lastName: string;
  preferredName: string | null;
  phone: string | null;
  mfaEnabled: boolean;
  createdAt: string;
  staffAccess: boolean;
  acceptances: Array<{ document: LegalDocumentKey; version: string; acceptedAt: string }>;
  legalAcceptanceRequired: boolean;
}

export interface CustomerAccountCompany {
  key: string;
  displayName: string;
  since: string;
  marketing: boolean;
}

export interface CustomerAccountView {
  account: CustomerAccount;
  companies: CustomerAccountCompany[];
}

export interface SignupInput {
  firstName: string;
  lastName: string;
  preferredName?: string | undefined;
  email: string;
  phone?: string | undefined;
  adult: boolean;
  termsVersion: string;
  privacyVersion: string;
}

export const accountApi = {
  signup: async (input: SignupInput) =>
    (await api.post<{ accepted: true; developmentToken?: string }>('/public/signup', input)).data,
  inspectSignup: async (token: string) =>
    (
      await api.get<{
        email: string;
        firstName: string;
        preferredName: string | null;
        mfaUri: string;
      }>(`/public/signup/${encodeURIComponent(token)}`)
    ).data,
  confirmSignup: async (token: string, input: { password: string; code?: string | undefined }) => {
    await api.post(`/public/signup/${encodeURIComponent(token)}/confirm`, {
      password: input.password,
      ...(input.code ? { code: input.code } : {}),
    });
  },
  confirmEmail: async (token: string) => {
    await api.post(`/public/email-change/${encodeURIComponent(token)}/confirm`);
  },
  get: async () => (await api.get<CustomerAccountView>('/customer/account')).data,
  update: async (input: {
    firstName: string;
    lastName: string;
    preferredName?: string | undefined;
    phone?: string | undefined;
  }) => (await api.put<CustomerAccountView>('/customer/account', input)).data,
  acceptLegal: async () =>
    (await api.post<CustomerAccountView>('/customer/account/legal-acceptance')).data,
  setMarketing: async (companyKey: string, granted: boolean) =>
    (
      await api.put<CustomerAccountView>(
        `/customer/account/companies/${encodeURIComponent(companyKey)}/marketing`,
        { granted },
      )
    ).data,
  requestEmailChange: async (input: { email: string; password: string }) =>
    (
      await api.post<{ accepted: true; developmentToken?: string }>(
        '/customer/account/email',
        input,
      )
    ).data,
  export: async () =>
    (await api.get<Blob>('/customer/account/export', { responseType: 'blob' })).data,
  delete: async (password: string) => {
    await api.post('/customer/account/deletion', { password });
  },
};
